import React, { forwardRef, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
// TODO react-input-mask is only necessary if we need a mask
// should be another dedicated component
import InputMask from "react-input-mask";
import useConstraintValidation from "../hooks/useConstraintValidation";
import { useUniqueId } from "../hooks/useUniqueId";
import { ThemeContext } from "../Theme";
import styles from "./Input.scss";
import { Icon } from "../Icon";
import { A11yText } from "../A11yText";

// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
export const Input = forwardRef((props, ref) => {
  const {
    children,
    onBeforeValueChange,
    onChange,
    onBlur,
    onInvalid,
    value,
    customClass,
    inputCustomClass,
    isValid: isValidOverride,
    invalidMessage,
    requiredMessage,
    required,
    hintMessage,
    button,
    mask,
    hasValueAlways,
    scheme,
    disabled,
    labelProps,
    ...restProps
  } = props;
  const theme = useContext(ThemeContext);

  const [inputRef, setInputRef] = useState({ current: null });
  const [inputValue, setInputValue] = useState(value || "");
  const {
    valueMissing,
    invalidNotMissing,
    valid: isValid,
    handleInvalid,
    onBlurValidityCheck,
    onChangeValidityCheck,
  } = useConstraintValidation(inputRef.current);

  const hasValidProp = typeof isValidOverride !== "undefined";
  const hasValue = hasValueAlways || inputValue.length;
  const iconColor = theme === "theme_dark" ? "white" : "black";
  const inputMaskClasses = cx(styles.input, {
    "is-active": hasValue,
    "is-invalid": (hasValidProp && !isValidOverride) || !isValid,
  });

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setInputValue(value);
    }
  }, [value]);

  // TODO START: Rethink this
  let className = scheme;

  if (!scheme) {
    className = styles.wrapper;
  }
  if (typeof scheme !== "string") {
    className = scheme.join(" ");
  }

  if (theme) {
    className += ` ${styles[theme]}`;
  }

  if (disabled) {
    className += ` ${styles.disabled}`;
  }

  if (customClass) {
    className += ` ${customClass}`;
  }

  // TODO END: Rethink this

  const handleChange = (e) => {
    let newValue = e.target.value;
    if (onBeforeValueChange) {
      newValue = onBeforeValueChange(newValue);
    }

    setInputValue(newValue);
    onChangeValidityCheck();

    e.target.value = newValue;

    if (onChange && typeof onChange === "function") {
      onChange(e);
    }
  };

  const handleBlur = (e) => {
    onBlurValidityCheck();
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleOnInvalid = (e) => {
    handleInvalid(e);

    if (onInvalid) {
      onInvalid(e);
    }
  };

  const shouldShowInvalidMessage = () =>
    (hasValidProp && !isValidOverride) || invalidNotMissing;
  const shouldShowRequiredMessage = () => valueMissing;

  const onButtonClick = (e) => {
    if (button && button.onClick && typeof button.onClick === "function") {
      button.onClick(e, inputRef.current);
    }
  };

  const controlledInputProps =
    value !== undefined && value !== null ? { value: inputValue } : {};

  const { id } = restProps;
  const inputId = id || `input-${useUniqueId("Input", props) + 1}`;

  return (
    <div className={className} ref={ref}>
      <>
        <div className={styles.innerWrapper}>
          <InputMask
            id={inputId}
            name={inputId}
            type="text"
            className={cx(inputMaskClasses, inputCustomClass)}
            onChange={handleChange}
            onBlur={handleBlur}
            onInvalid={handleOnInvalid}
            inputRef={(e) => {
              setInputRef({ current: e });
            }}
            aria-invalid={(hasValidProp && !isValidOverride) || !isValid}
            aria-describedby={
              (hasValidProp && !isValidOverride) || !isValid
                ? `msg-${inputId}`
                : null
            }
            mask={mask ? mask.mask : undefined}
            maskChar={mask ? mask.char : undefined}
            disabled={disabled}
            required={required}
            {...controlledInputProps}
            {...restProps}
          />

          {button && (
            <button
              type="button"
              className={cx(styles.button, button.customClass)}
              onClick={onButtonClick}
            >
              {button.icon && (
                <Icon
                  icon={button.icon}
                  color={iconColor}
                  size={Icon.Size.parent}
                />
              )}
              {button.label && <A11yText>{button.label}</A11yText>}
            </button>
          )}

          <label htmlFor={inputId} className={styles.label} {...labelProps}>
            {children}
            {required ? "*" : null}
          </label>
        </div>

        {hintMessage && (
          <p id={`hint-${inputId}`} className={styles.hint}>
            {hintMessage}
          </p>
        )}

        {shouldShowInvalidMessage() && (
          <p id={`msg-${inputId}`} className={styles.message}>
            {invalidMessage}
          </p>
        )}
        {shouldShowRequiredMessage() && (
          <p id={`msg-${inputId}`} className={styles.message}>
            {requiredMessage}
          </p>
        )}
      </>
    </div>
  );
});

Input.Variant = {
  themeDark: "input--theme_dark",
};

Input.defaultProps = {
  value: undefined,
  scheme: "",
  customClass: undefined,
  inputCustomClass: undefined,
  isValid: undefined,
  disabled: false,
  invalidMessage: undefined,
  requiredMessage: undefined,
  required: false,
  hintMessage: undefined,
  button: undefined,
  mask: undefined,
  onBeforeValueChange: null,
  onChange: null,
  onBlur: null,
  onInvalid: null,
  hasValueAlways: false,
  labelProps: {},
};

Input.propTypes = {
  value: PropTypes.string,
  isValid: PropTypes.bool,
  disabled: PropTypes.bool,
  invalidMessage: PropTypes.string,
  requiredMessage: PropTypes.string,
  required: PropTypes.bool,
  customClass: PropTypes.string,
  inputCustomClass: PropTypes.string,
  hintMessage: PropTypes.string,
  // TODO button should not be passed as prop
  button: PropTypes.shape({
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    customClass: PropTypes.string,
  }),
  // TODO should probably be splitted into two components
  mask: PropTypes.shape({
    mask: PropTypes.string.isRequired,
    char: PropTypes.string.isRequired,
  }),
  onChange: PropTypes.func,
  onBeforeValueChange: PropTypes.func,
  onBlur: PropTypes.func,
  onInvalid: PropTypes.func,
  // TODO label as children does not make sense, multiple labels are not possible
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  // TODO one scheme can be string or array? sounds strange
  // TODO scheme is responsible for styles... wtf... weird name?
  scheme: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  // TODO couldn't find out what this here changes
  hasValueAlways: PropTypes.bool,
  labelProps: PropTypes.objectOf(PropTypes.any),
};

Input.displayName = "Input";

export default Input;
